/**
 * EncounterAuditRow Component
 *
 * Single row displaying encounter audit summary information.
 * Renders as a CSS Grid row in table layouts or as a stacked card on mobile.
 */

import React, { useMemo } from 'react';
import { Avatar } from '@jotpsych/ui';
import {
  ChevronRight as ChevronIcon,
  EventNote as EncounterIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { EncounterAuditSummary } from '../../types';
import { QualityScore } from '../shared/QualityScore';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPatientNameParts(
  patient: { first_name?: string | null; last_name?: string | null } | null | undefined,
  patientName: string | null | undefined,
): { firstName: string; lastName: string } {
  if (patient?.first_name || patient?.last_name) {
    return { firstName: patient.first_name || '', lastName: patient.last_name || '' };
  }
  if (!patientName) return { firstName: '', lastName: '' };
  const parts = patientName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function getProviderNameParts(
  providerName: string | null | undefined,
): { firstName: string; lastName: string } {
  if (!providerName) return { firstName: '', lastName: '' };
  const parts = providerName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

// Canonical service_date arrives as a date-only string ("YYYY-MM-DD"). Anchor it
// to local midnight so it renders as the same calendar day everywhere (a bare
// `new Date("2026-05-19")` parses as UTC midnight and can render the prior day).
const formatServiceDate = (dateOnly: string | null | undefined): string => {
  if (!dateOnly) return '—';
  try {
    const date = new Date(`${dateOnly}T00:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const formatTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

const getModalityIcon = (modality: string | null | undefined) => {
  switch (modality) {
    case 'video':
      return <VideoIcon fontSize="small" />;
    case 'audio':
      return <PhoneIcon fontSize="small" />;
    case 'in_person':
      return <PersonIcon fontSize="small" />;
    default:
      return <EncounterIcon fontSize="small" />;
  }
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EncounterAuditRowProps {
  encounter: EncounterAuditSummary;
  onViewEncounter?: (encounterId: string) => void;
  /** Layout mode controlling which columns are visible */
  layout: TableLayoutMode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EncounterAuditRow: React.FC<EncounterAuditRowProps> = ({
  encounter,
  onViewEncounter,
  layout,
}) => {
  const theme = useTheme();

  const handleRowClick = () => {
    onViewEncounter?.(encounter.encounter_id);
  };

  // ---- Derived data ----

  const compliancePct = encounter.latest_compliance_pct;
  const hasAudit = encounter.has_audit;

  const patientNameParts = useMemo(
    () => getPatientNameParts(encounter.patient, encounter.patient_name),
    [encounter.patient, encounter.patient_name],
  );

  const providerNameParts = useMemo(
    () => getProviderNameParts(encounter.provider_name),
    [encounter.provider_name],
  );

  const displayName = useMemo(() => {
    if (encounter.patient?.preferred_name) return encounter.patient.preferred_name;
    if (encounter.patient?.first_name) {
      return [encounter.patient.first_name, encounter.patient.last_name]
        .filter(Boolean)
        .join(' ');
    }
    return encounter.patient_name || 'Patient';
  }, [encounter.patient, encounter.patient_name]);

  const hasPatient = Boolean(encounter.patient_id);
  const hasProvider = Boolean(encounter.provider_name);
  // GDN-2017: the DATE shown is the canonical resolved service_date (the billing
  // day), not the recording clock. The clock (start_time/scheduled_for) is kept
  // only for the time-of-day read-out, and only when it actually falls on the
  // service date — so a dictation recorded weeks after the visit shows the visit
  // date with no misleading time. Legacy rows with no service_date fall back to
  // the prior clock-based behavior.
  const dateTimeSource = encounter.appointment?.start_time ?? encounter.scheduled_for;
  const serviceDateStr = encounter.service_date ?? null;
  const displayDateText = serviceDateStr
    ? formatServiceDate(serviceDateStr)
    : formatDate(dateTimeSource);
  const clockMatchesServiceDate =
    !serviceDateStr ||
    (dateTimeSource
      ? new Date(dateTimeSource).toDateString() ===
        new Date(`${serviceDateStr}T00:00:00`).toDateString()
      : false);
  const displayTimeText = clockMatchesServiceDate ? formatTime(dateTimeSource) : '';
  // Prefer canonical_service_type.name, fall back to session_type.canonical
  const serviceType =
    (encounter as any).canonical_service_type?.name ||
    (encounter as any).encounter_analysis?.canonical_service_type?.name ||
    encounter.session_type?.canonical ||
    null;

  // CPT analysis
  const cptAnalysis = (encounter as any).cpt_analysis;
  const targetCodes: string[] | undefined = cptAnalysis?.target_cpt_codes;
  const cptSource: string | undefined = cptAnalysis?.target_cpt_source;
  const cptMatch: boolean | undefined = cptAnalysis?.cpt_match;

  let cptBadgeColor: string = theme.palette.info.main;
  let cptTooltip = '';

  if (cptSource === 'user') {
    if (cptMatch === true) {
      cptBadgeColor = theme.palette.success.main;
      cptTooltip = 'Audit based on your codes (matches documentation)';
    } else if (cptMatch === false) {
      cptBadgeColor = theme.palette.warning.main;
      cptTooltip = 'Audit based on your codes (AI suggests different based on documentation)';
    }
  } else if (cptSource === 'ai') {
    cptBadgeColor = theme.palette.info.main;
    cptTooltip = 'Audit based on AI-suggested codes from documentation';
  }

  const showServiceType = layout === 'full' || layout === 'standard';
  const showBillingCodes = layout === 'full' || layout === 'standard';
  const showQuality = layout === 'full';

  // Calculate summary left padding based on columns before content
  // Icon column + Date column + gap widths that precede the content area
  // Must be declared before the early return for card layout to avoid breaking hook order
  const summaryPadding = useMemo(() => {
    switch (layout) {
      case 'full':
        return '192px';
      case 'standard':
        return '182px';
      case 'compact':
        return '172px';
      default:
        return '192px';
    }
  }, [layout]);

  // ---- Card layout (mobile) ----

  if (layout === 'card') {
    return (
      <Box
        onClick={handleRowClick}
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          cursor: onViewEncounter ? 'pointer' : 'default',
          transition: 'background-color 0.15s ease',
          '&:hover': onViewEncounter ? { bgcolor: theme.palette.grey[50] } : {},
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        {/* Top row: Icon + Date/Time + Status + Chevron */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          {/* Modality icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              flexShrink: 0,
            }}
          >
            {getModalityIcon(encounter.modality)}
          </Box>

          {/* Date & time */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {displayDateText}
              {displayTimeText && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, ml: 0.5 }}
                >
                  · {displayTimeText}
                </Typography>
              )}
              {encounter.appointment?.duration && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, ml: 0.5 }}
                >
                  · {encounter.appointment.duration} min
                </Typography>
              )}
            </Typography>
          </Box>

          {/* Status chip */}
          {encounter.note_id && (
            <Chip
              label={(encounter as any).is_signed ? 'Signed' : 'Draft'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: (encounter as any).is_signed
                  ? `${theme.palette.success.main}15`
                  : `${theme.palette.grey[500]}15`,
                color: (encounter as any).is_signed
                  ? theme.palette.success.main
                  : theme.palette.grey[600],
                border: (encounter as any).is_signed
                  ? `1px solid ${theme.palette.success.main}50`
                  : `1px solid ${theme.palette.grey[400]}50`,
                '& .MuiChip-label': { px: 0.75 },
                flexShrink: 0,
              }}
            />
          )}

          {/* Chevron */}
          {onViewEncounter && (
            <ChevronIcon fontSize="small" sx={{ color: theme.palette.grey[400], flexShrink: 0 }} />
          )}
        </Box>

        {/* People row: Provider → Patient */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.75, flexWrap: 'wrap' }}>
          {hasProvider && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Avatar
                id={encounter.provider_name || 'unknown'}
                firstName={providerNameParts.firstName}
                lastName={providerNameParts.lastName}
                sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {encounter.provider_name}
              </Typography>
            </Box>
          )}

          {hasProvider && hasPatient && (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.75rem' }}>
              →
            </Typography>
          )}

          {hasPatient && encounter.patient_id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Avatar
                id={encounter.patient_id}
                firstName={patientNameParts.firstName}
                lastName={patientNameParts.lastName}
                sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Metadata row: Service type, Billing codes, Compliance, Quality */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {serviceType && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '50%',
              }}
            >
              {serviceType}
            </Typography>
          )}

          {targetCodes && targetCodes.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {targetCodes.slice(0, 2).map((code: string, idx: number) => (
                <Chip
                  key={idx}
                  label={code}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    bgcolor: `${cptBadgeColor}15`,
                    color: cptBadgeColor,
                    border: `1px solid ${cptBadgeColor}50`,
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              ))}
            </Box>
          )}

          {hasAudit && compliancePct !== null && compliancePct !== undefined && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color:
                  compliancePct >= 80
                    ? theme.palette.success.main
                    : compliancePct >= 60
                      ? theme.palette.warning.main
                      : theme.palette.error.main,
              }}
            >
              {compliancePct.toFixed(0)}%
            </Typography>
          )}

          {hasAudit && (
            <QualityScore
              score={encounter.latest_quality_score}
              variant="badge"
              size="small"
            />
          )}
        </Box>

        {/* Summary */}
        {encounter.summary && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              color: theme.palette.text.disabled,
              lineHeight: 1.4,
              fontSize: '0.8rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {encounter.summary}
          </Typography>
        )}
      </Box>
    );
  }

  // ---- Table-row layout (full / standard / compact) ----

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onViewEncounter ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        '&:hover': onViewEncounter ? { bgcolor: theme.palette.grey[50] } : {},
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Grid row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGridTemplateColumns(layout),
          gap: GRID_GAP,
          alignItems: 'start',
        }}
      >
        {/* Encounter Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
          }}
        >
          {getModalityIcon(encounter.modality)}
        </Box>

        {/* Date & Time */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {displayDateText}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
            {displayTimeText}
          </Typography>
          {encounter.appointment?.duration && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
              {encounter.appointment.duration} min
            </Typography>
          )}
        </Box>

        {/* Provider with Avatar */}
        <Box sx={{ minWidth: 0 }}>
          {hasProvider ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                id={encounter.provider_name || 'unknown'}
                firstName={providerNameParts.firstName}
                lastName={providerNameParts.lastName}
                sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 600, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={encounter.provider_name || undefined}
                >
                  {encounter.provider_name}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
          )}
        </Box>

        {/* Patient with Avatar */}
        <Box sx={{ minWidth: 0 }}>
          {hasPatient && encounter.patient_id ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                id={encounter.patient_id}
                firstName={patientNameParts.firstName}
                lastName={patientNameParts.lastName}
                sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 600, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={displayName}
                >
                  {displayName}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
          )}
        </Box>

        {/* Service Type - hidden on compact */}
        {showServiceType && (
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: theme.palette.text.secondary,
              }}
              title={serviceType || undefined}
            >
              {serviceType || '—'}
            </Typography>
          </Box>
        )}

        {/* Billing Codes - hidden on compact */}
        {showBillingCodes && (
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40, gap: 0.5, flexWrap: 'wrap' }}>
            {targetCodes && targetCodes.length > 0 ? (
              <Tooltip title={cptTooltip} arrow placement="top">
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {targetCodes.slice(0, 2).map((code: string, idx: number) => (
                    <Chip
                      key={idx}
                      label={code}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        bgcolor: `${cptBadgeColor}15`,
                        color: cptBadgeColor,
                        border: `1px solid ${cptBadgeColor}50`,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  ))}
                </Box>
              </Tooltip>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
            )}
          </Box>
        )}

        {/* Signed Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
          {encounter.note_id ? (
            <Chip
              label={(encounter as any).is_signed ? 'Signed' : 'Draft'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: (encounter as any).is_signed
                  ? `${theme.palette.success.main}15`
                  : `${theme.palette.grey[500]}15`,
                color: (encounter as any).is_signed
                  ? theme.palette.success.main
                  : theme.palette.grey[600],
                border: (encounter as any).is_signed
                  ? `1px solid ${theme.palette.success.main}50`
                  : `1px solid ${theme.palette.grey[400]}50`,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
          )}
        </Box>

        {/* Compliance % */}
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
          {hasAudit && compliancePct !== null && compliancePct !== undefined ? (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color:
                  compliancePct >= 80
                    ? theme.palette.success.main
                    : compliancePct >= 60
                      ? theme.palette.warning.main
                      : theme.palette.error.main,
              }}
            >
              {compliancePct.toFixed(0)}%
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
          )}
        </Box>

        {/* Quality Score - hidden on compact */}
        {showQuality && (
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
            {hasAudit ? (
              <QualityScore
                score={encounter.latest_quality_score}
                variant="badge"
                size="small"
              />
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
            )}
          </Box>
        )}

        {/* Chevron */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minHeight: 40,
          }}
        >
          {onViewEncounter && (
            <ChevronIcon fontSize="small" sx={{ color: theme.palette.grey[400] }} />
          )}
        </Box>
      </Box>

      {/* Summary row */}
      {encounter.summary && (
        <Box
          sx={{
            mt: 1,
            pl: summaryPadding,
            pr: '40px',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.disabled,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {encounter.summary}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
